// src/pages/productions/utils/excelImportDailyProduction.js
import * as XLSX from "xlsx";

// Helper function untuk format tanggal DD-MM-YYYY
const formatDateToDDMMYYYY = (date) => {
    if (!date) return '';
    
    let dateObj;
    
    // Handle berbagai tipe input date
    if (date instanceof Date) {
        dateObj = date;
    } else if (typeof date === 'string') {
        dateObj = new Date(date);
    } else if (typeof date === 'number') {
        // Excel date number
        dateObj = new Date((date - 25569) * 86400 * 1000);
    } else {
        return String(date);
    }
    
    // Validasi date
    if (isNaN(dateObj.getTime())) {
        return String(date);
    }
    
    // Format ke DD-MM-YYYY
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const year = dateObj.getFullYear();
    
    return `${day}-${month}-${year}`;
};

// Helper function untuk format angka dengan 4 digit desimal sebagai string
const formatToFourDecimalString = (value) => {
    if (value === undefined || value === null || value === '') {
        return '0.0000';
    }
    
    // Konversi ke number terlebih dahulu
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    
    // Jika bukan angka yang valid, return 0.0000
    if (isNaN(num)) {
        return '0.0000';
    }
    
    // Format ke 4 digit desimal
    return num.toFixed(4);
};

// Helper: ubah onValue menjadi Promise
export const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        const { onValue } = require("firebase/database");
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Fungsi import Excel khusus untuk Daily Production Data sesuai struktur yang diberikan
export const importExcelToFirebaseDailyProduction = async (file, database, onValuePromise) => {
    const reader = new FileReader();
    
    return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                
                // **Sama seperti di excelImportHandler.js: raw: true**
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: true });
                
                const dataStartRow = 5;
                
                // **DEBUG seperti di excelImportHandler.js**
                console.log("Sample raw data dari Excel:");
                if (jsonData.length > dataStartRow) {
                    console.log("Row 6 raw:", jsonData[dataStartRow]);
                    console.log("id_lokasi value type:", typeof jsonData[dataStartRow][1]);
                    console.log("id_lokasi value:", jsonData[dataStartRow][1]);
                }

                // Mapping kolom
                const columnMapping = {
                    id_lokasi: 0,
                    sloc: 3,
                    packing_date: 4,
                    material_code: 5,
                    batch: 6,
                    sum_of_qty: 7,
                    sum_of_weight: 8
                };

                const { ref, push, update } = require("firebase/database");
                const productionRef = ref(database, "db_productions/tb_daily_production");
                const existingProductionSnapshot = await onValuePromise(productionRef);
                const existingProductionData = existingProductionSnapshot.val() || {};

                const existingProductionMap = new Map();
                Object.keys(existingProductionData).forEach(fbKey => {
                    const record = existingProductionData[fbKey];
                    const uniqueKey = `${record.id_lokasi}_${record.batch}_${record.packing_date}`;
                    if (record.id_lokasi && record.batch && record.packing_date) {
                        existingProductionMap.set(uniqueKey, fbKey);
                    }
                });

                const importTimestamp = new Date().toISOString();

                let importedCount = 0;
                let updatedCount = 0;
                let ignoredCount = 0;

                // Proses data
                for (let i = dataStartRow; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (!row || row.length === 0) {
                        ignoredCount++;
                        continue;
                    }
                    
                    // **Sama seperti di excelImportHandler.js: ambil langsung dari row[]**
                    const id_lokasi = row[columnMapping.id_lokasi];
                    const sloc = row[columnMapping.sloc];
                    const packing_date = row[columnMapping.packing_date];
                    const material_code = row[columnMapping.material_code];
                    const batch = row[columnMapping.batch];
                    const sum_of_qty = row[columnMapping.sum_of_qty];
                    const sum_of_weight = row[columnMapping.sum_of_weight];
                    
                    // **Validasi sama seperti di excelImportHandler.js**
                    if (id_lokasi === undefined || id_lokasi === null) {
                        console.log(`Baris ${i + 1}: id_lokasi undefined/null, diabaikan`);
                        ignoredCount++;
                        continue;
                    }

                    // **Format sama seperti di excelImportHandler.js**
                    const formattedIdLokasi = String(id_lokasi).trim();
                    const formattedBatch = batch ? String(batch).trim() : '';

                    // Format tanggal
                    const formattedPackingDate = formatDateToDDMMYYYY(packing_date);

                    const uniqueKey = `${formattedIdLokasi}_${formattedBatch}_${formattedPackingDate}`;

                    // Format nilai
                    const sumOfQtyAsString = String(sum_of_qty).trim();
                    const sumOfWeightAsString = formatToFourDecimalString(sum_of_weight);

                    // Data untuk Firebase
                    const excelDataToUpdate = {
                        id_lokasi: formattedIdLokasi,
                        sloc: sloc ? String(sloc).trim() : '',
                        packing_date: formattedPackingDate,
                        material_code: material_code ? String(material_code).trim() : '',
                        batch: formattedBatch,
                        sum_of_qty: sumOfQtyAsString,
                        sum_of_weight: sumOfWeightAsString,
                        last_imported_at: importTimestamp,
                    };

                    const firebaseKey = existingProductionMap.get(uniqueKey);

                    try {
                        if (firebaseKey) {
                            await update(ref(database, `db_productions/tb_daily_production/${firebaseKey}`), excelDataToUpdate);
                            updatedCount++;
                        } else {
                            await push(productionRef, excelDataToUpdate);
                            importedCount++;
                        }
                    } catch (error) {
                        console.error(`Error baris ${i + 1}:`, error);
                        ignoredCount++;
                    }
                }

                console.log(`Hasil: ${importedCount} baru, ${updatedCount} update, ${ignoredCount} abaikan`);

                resolve({
                    success: true,
                    importedCount,
                    updatedCount,
                    ignoredCount,
                    total: importedCount + updatedCount
                });

            } catch (error) {
                console.error("Gagal impor Excel:", error);
                reject({
                    success: false,
                    error: "Terjadi kesalahan saat mengimpor data dari Excel.",
                    details: error.message
                });
            }
        };

        reader.onerror = () => {
            reject({
                success: false,
                error: "Gagal membaca file."
            });
        };

        reader.readAsArrayBuffer(file);
    });
};

// Export helper functions untuk digunakan di komponen lain jika diperlukan
export { formatDateToDDMMYYYY, formatToFourDecimalString };